import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IModule, ISegment } from "redux/reducers/models/reservations";

interface IGetReservationModule {
  facility_short_name?: string;
  handle?: string;
  id?: number;
}

interface IPostReservationModule {
  title: string;
  enable_web_booking: boolean;
  web_cancellation_hours: number;
  web_edit_hours: number;
  days_in_advance_start: number;
  days_in_advance_end: number;
  open_time: string;
  buffer_window_hours: number;
  max_booking_size?: number;
  require_booking_approval?: boolean;
}

interface IPutReservationModule {
  id: number;
  title: string;
  enable_web_booking: boolean;
  web_cancellation_hours: number;
  web_edit_hours: number;
  days_in_advance_start: number;
  days_in_advance_end: number;
  open_time: string;
  booking_terms: string;
  payment_terms: string;
  display_online_pricing: boolean;
  buffer_window_hours: string;
  max_booking_size?: number;
  require_booking_approval?: boolean;
}

interface IGetReservationLocation {
  id?: number;
  facility_id?: number;
  reservation_module_id?: number;
}

interface IPostReservationLocation {
  title: string;
  reservation_module_id: number;
}

interface IPutReservationLocation extends IPostReservationLocation {
  id: number;
}

interface IGetReservationSegment {
  id?: number;
  date?: string;
  start_time?: string;
  location_id?: number;
  extended?: boolean;
  module_id?: number;
}

interface IPostReservationBooking {
  customer_id: number;
  module_id: number;
  location_id: number;
  segment_ids: Array<number>;
  date: string;
  quantity: number;
  customer_payment_method_id: number;
  reservation_participants: Array<{ customer_id: number; guest: boolean }>;
}

interface IPostReservationConfig {
  // location_ids: Array<number>;
  start_time: string;
  end_time: string;
  date: string;
  interval_sections: Array<IIntervalSection>;
}
interface IPutReservationBooking {
  id: number;
  segment_ids?: Array<number>;
  date?: string;
  quantity?: number;
  customer_payment_method_id?: number | null;
  check_in_status?: string;
  order_financial_status?: string;
  order_id?: number | null;
  order_name?: string | null;
  table_id?: number;
  reservation_participants?: Array<{ id?: number; customer_id: number; guest: boolean; check_in_status?: string }>; //Update reservation participants
}

interface IMoveReservationBooking {
  booking_id: number;
  location_id: number;
  date: string;
  segment_ids: number[];
}

interface IGetReservationBooking {
  id: number;
  extended?: boolean;
}

interface IGetReservationBookingAll {
  customer_id?: number;
}

interface IPutReservationSegment {
  segments: Array<ISegment>;
}

interface IDeleteReservationSegment {
  segment_ids: Array<number>;
}

interface IPutReservationBookingConfirmation {
  reservation_booking_id: number;
}

interface IGetReservationNotes {
  id: number;
}

interface IGetReservationNotesAll {
  reservation_booking_id: number;
}

interface IPostReservationBookingNote {
  reservation_booking_id: number;
  content?: string;
}

interface IGetReservationTemplate {
  id?: number;
  module_id?: number;
}

export interface ILocationSchedule {
  location_id: number;
  interval_sections: Array<IIntervalSection>;
}

interface IPostReservationTemplate {
  module_id: number;
  name: string;
  location_ids?: Array<number>;
  locations?: Array<ILocationSchedule>;
  web_booking_enabled: boolean;
  credit_card_required: boolean;
  start_time: string;
  end_time: string;
  interval_sections?: Array<IIntervalSection>;
}

interface IPutReservationTemplate {
  segments: Array<ISegment>;
}

interface IPutApplyReservationTemplate {
  date?: string;
  template_id?: number;
  template_data?: Array<{ date: string; template_id: number }>;
}

export interface IIntervalSection {
  start_time: string;
  end_time: string;
  interval: number;
}

export async function GetReservationModule(params: IGetReservationModule, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/module"), params, useGlobalLoader);
}

export async function PostReservationModule(params: IPostReservationModule, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/module"), params, useGlobalLoader);
}

export async function PutReservationModule(params: IPutReservationModule, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/module"), params, useGlobalLoader);
}

export async function DeleteReservationModule(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/reservation/module"), params, useGlobalLoader);
}

export async function GetReservationModuleAll(
  params: IGetReservationModule,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<IModule[]>(
    APIUrl(AdminType.FacilityAdmin, "/reservation/module/all"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostReservationLocation(params: IPostReservationLocation, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/location"), params, useGlobalLoader);
}

export async function PutReservationLocation(params: IPutReservationLocation, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/location"), params, useGlobalLoader);
}

export async function DeleteReservationLocation(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/reservation/location"), params, useGlobalLoader);
}

export async function GetReservationLocation(
  params: IGetReservationLocation,
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/location"), params, useGlobalLoader, cancelToken);
}

export async function GetAllReservationLocation(useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/location/all"), null, useGlobalLoader, cancelToken);
}

export async function GetReservationSegment(params: IGetReservationSegment, useGlobalLoader: boolean) {
  return await APIGet<ISegment[]>(APIUrl(AdminType.FacilityAdmin, "/reservation/segment"), params, useGlobalLoader);
}

export async function GetSegmentAuditTrail(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/segment/audit"), params, useGlobalLoader);
}

export async function GetReservationBooking(params: IGetReservationBooking, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/booking"), params, useGlobalLoader);
}
export async function GetPrintReservation(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/segment/print"), params, useGlobalLoader);
}

export async function GetReservationBookingAll(
  params: IGetReservationBookingAll,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/all"), params, useGlobalLoader, token);
}

export async function PostReservationBooking(params: IPostReservationBooking, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/booking"), params, useGlobalLoader);
}

export async function PostReservationSegmentConfig(params: IPostReservationConfig, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/segment/config"), params, useGlobalLoader);
}

export async function PutReservationBooking(params: IPutReservationBooking, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking"), params, useGlobalLoader);
}

export async function MoveReservationBooking(params: IMoveReservationBooking, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/move"), params, useGlobalLoader);
}

export async function PutReservationBookingConfirmation(
  params: IPutReservationBookingConfirmation,
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/confirmation"), params, useGlobalLoader);
}

export async function PutAcceptReservationBooking(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/accept"), params, useGlobalLoader);
}

export async function PutCancelReservationBooking(
  params: { id: number; send_cancellation_email?: boolean },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/cancel"), params, useGlobalLoader);
}

export async function PutReservationSegment(params: IPutReservationSegment, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/segment"), params, useGlobalLoader);
}

export async function DeleteReservationSegment(params: IDeleteReservationSegment, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/reservation/segment"), params, useGlobalLoader);
}

//Booking Notes

/**Get a single reservation booking note base on the note's id */
export async function GetReservationBookingNote(params: IGetReservationNotes, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/note"), params, useGlobalLoader);
}

/**Get all booking notes attached to a booking based on reservation booking id */
export async function GetReservationBookingNoteAll(params: IGetReservationNotesAll, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/note/all"), params, useGlobalLoader);
}

export async function PostReservationBookingNote(params: IPostReservationBookingNote, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/note"), params, useGlobalLoader);
}

//Mark the reservation booking linked to a table as paid
export async function PutMarkPaidReservationBooking(params: { table_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/mark-paid"), params, useGlobalLoader);
}

// Notifications

export interface IReservationNotification {
  id: number;
  module_id: string;
  date?: string;
  subject: string;
  content: string;
  type: string;
  user_id: number;
  user_type: "admin" | "customer";
}

interface IGetReservationNotificationAll {
  module_id?: number;
  date?: string;
  user_type?: "admin" | "customer";
  limit?: number;
  offset?: number;
  facility_id?: number;
}

interface IPostReservationNotification {
  module_id: number;
  start_date: string;
  end_date: string;
  user_type: "admin" | "customer";
  subject: string;
  content: string;
  type: "forced" | "passive";
}

interface IPutReservationNotification {
  user_type: "admin" | "customer";
  subject: string;
  content: string;
  type: "forced" | "passive";
}

interface IDeleteReservationNotification {
  id: number;
}

interface IGetReservationNotification {
  id: number;
}

/**Send an email to selected booking ids */
export async function PostReservationEmail(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/notification/email"), params, useGlobalLoader);
}

export async function GetReservationNotification(
  params: IGetReservationNotification,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/notification"), params, useGlobalLoader, token);
}
export async function GetReservationNotificationAll(
  params: IGetReservationNotificationAll,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/notification/all"), params, useGlobalLoader, token);
}

export async function GetCustomerReservationNotificationAll(
  params: IGetReservationNotificationAll,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.Customer, "/reservation/notification"), params, useGlobalLoader, token);
}

export async function GetGuestReservationNotificationAll(
  params: IGetReservationNotificationAll,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.Guest, "/reservation/notification"), params, useGlobalLoader, token);
}

export async function PostReservationNotification(params: IPostReservationNotification, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/notification"), params, useGlobalLoader);
}

export async function PutReservationNotification(params: IPutReservationNotification, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/notification"), params, useGlobalLoader);
}

export async function DeleteReservationNotification(params: IDeleteReservationNotification, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/reservation/notification"), params, useGlobalLoader);
}

// Templates
export async function GetReservationTemplate(
  params: IGetReservationTemplate,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/reservation/template"), params, useGlobalLoader, token);
}

export interface IReservationCalendarDay {
  date: string;
  template_id: number | null;
  template_name: string | null;
  template_version: number | null;
  scheduled: boolean;
}

export async function GetReservationTemplateCalendar(
  params: { module_id: number; year: number; month: number },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<{ days: IReservationCalendarDay[] }>(
    APIUrl(AdminType.FacilityAdmin, "/reservation/template/calendar"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostReservationTemplate(params: IPostReservationTemplate, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/reservation/template"), params, useGlobalLoader);
}

export async function PutReservationTemplate(params: IPutReservationTemplate, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/template"), params, useGlobalLoader);
}

export async function PutApplyReservationTemplate(params: IPutApplyReservationTemplate, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/template/apply"), params, useGlobalLoader);
}

export async function PutCopyReservationBooking(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/booking/copy"), params, useGlobalLoader);
}

interface IPutTemplateSegments {
  template_id: number;
  location_id: number;
  interval_sections: IIntervalSection[];
}

export async function PutTemplateSegments(params: IPutTemplateSegments, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/reservation/template/overwrite"), params, useGlobalLoader);
}

export async function DeleteReservationTemplateSegments(
  params: { template_id: number; selectedSegments: Array<number> },
  useGlobalLoader: boolean,
) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/reservation/template/delete"), params, useGlobalLoader);
}
